import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Stack, Button, TextField,
  CircularProgress, Alert, Backdrop,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Loader from "../../Components/Loader/Loader";
import {
  fetchPublicEvent, submitPublicRegistration,
  PublicEventPayload, formatEventDate,
} from "./specialEventsApi";

/* ──────────────────────────────────────────────────────────────────────
   Public event registration — opened from a QR code on flyers, posters,
   or social media. Zero auth. Standalone layout (no sidebar / no app
   chrome) so it works as a clean landing experience for a guest who
   has never seen the rest of the product.

   Design intent: editorial luxury-venue feel — navy + champagne, sharp
   rectangles (no rounding), serif display type, hairline borders, a
   small gold ornament instead of plain dividers.
   ──────────────────────────────────────────────────────────────────── */

const NAVY = "#0d1b2a";
const NAVY_DEEP = "#0a1421";
const GOLD = "#c69c6a";
const GOLD_DEEP = "#a87e4f";
const IVORY = "#faf8f4";
const CREAM = "#f5f1e8";
const HAIRLINE = "rgba(13,27,42,0.08)";
const HAIRLINE_GOLD = "rgba(198,156,106,0.28)";

const FONT_SERIF = `'Fraunces', 'GT Sectra', 'Tiempos Headline', Georgia, serif`;
const FONT_SANS = `'Hanken Grotesk', 'Inter', system-ui, sans-serif`;

/** Field styling reused across every TextField — sharp corners, gold focus. */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    bgcolor: "#fff",
    transition: "background-color 0.2s ease",
    "& fieldset": { borderColor: HAIRLINE, borderWidth: 1, transition: "border-color 0.2s" },
    "&:hover fieldset": { borderColor: "rgba(13,27,42,0.25)" },
    "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 2 },
    "&.Mui-error fieldset": { borderColor: "#c62828" },
    "&.Mui-error.Mui-focused fieldset": { borderColor: "#c62828", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(13,27,42,0.55)",
    fontFamily: FONT_SANS,
    letterSpacing: 0.2,
    "&.Mui-focused": { color: GOLD_DEEP },
    "&.Mui-error, &.Mui-error.Mui-focused": { color: "#c62828" },
  },
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
    color: NAVY, fontFamily: FONT_SANS,
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: "#c62828", marginLeft: 0, fontSize: "0.78rem", letterSpacing: 0.2,
  },
};

/* ── Field-level validators — used both on blur (per-field) and on submit
   (to surface the first overall error). Each returns null when valid. ── */

const validateEmail = (v: string): string | null => {
  if (!v.trim()) return "Please enter your email.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Please enter a valid email address.";
  return null;
};

const validatePhone = (v: string): string | null => {
  if (!v.trim()) return "Please enter your cell number.";
  const digits = v.replace(/\D/g, "");
  if (digits.length < 10) return "Please enter a valid 10-digit phone number.";
  if (digits.length > 11) return "Please enter a valid phone number.";
  return null;
};

const validatePostal = (v: string): string | null => {
  if (!v.trim()) return "Please enter your postal code.";
  if (!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(v.trim())) {
    return "Please enter a valid Canadian postal code (e.g. L1Z 1C9).";
  }
  return null;
};

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  organization: string;
  business_type: string;
  /** Held as string in form state, parsed to int at submit time. */
  guest_count: string;
  postal_code: string;
  address: string;
  notes: string;
  /** Honeypot — invisible to humans, bots fill everything. */
  website: string;
}

const emptyForm: FormState = {
  full_name: "", email: "", phone: "", organization: "",
  business_type: "", guest_count: "1",
  postal_code: "", address: "", notes: "", website: "",
};

const PublicEventRegistration: React.FC = () => {
  const { slug = "" } = useParams<{ slug: string }>();

  const [event, setEvent]     = useState<PublicEventPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm]   = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess]   = useState(false);

  /** Fields that have lost focus at least once — only show inline errors
      once the user has interacted with the field. */
  const [touched, setTouched] = useState<{ email: boolean; phone: boolean; postal_code: boolean }>({
    email: false, phone: false, postal_code: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError(null);
      try {
        const data = await fetchPublicEvent(slug);
        if (!cancelled) setEvent(data);
      } catch (e: any) {
        if (!cancelled) setLoadError(e.message ?? "Could not load this event.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleBlur = (field: keyof typeof touched) => () =>
    setTouched((t) => ({ ...t, [field]: true }));

  /** Inline errors — only after the field has been touched. Recomputes
      on every render so the error clears as soon as the user fixes it. */
  const emailError  = touched.email       ? validateEmail(form.email)        : null;
  const phoneError  = touched.phone       ? validatePhone(form.phone)        : null;
  const postalError = touched.postal_code ? validatePostal(form.postal_code) : null;

  const validate = (): string | null => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    const e1 = validateEmail(form.email);   if (e1) return e1;
    const e2 = validatePhone(form.phone);   if (e2) return e2;
    if (!form.organization.trim()) return "Please enter your organization or affiliation.";
    if (!form.business_type.trim()) return "Please enter your type of business.";
    const n = parseInt(form.guest_count, 10);
    if (!Number.isFinite(n) || n < 1) return "Please enter a valid number of guests.";
    if (n > 50) return "For groups larger than 50, please contact us directly.";
    const e3 = validatePostal(form.postal_code); if (e3) return e3;
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setTouched({ email: true, phone: true, postal_code: true });
    const v = validate();
    if (v) { setSubmitError(v); return; }
    setSubmitting(true);
    try {
      await submitPublicRegistration(slug, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        organization: form.organization.trim() || undefined,
        business_type: form.business_type.trim() || undefined,
        guest_count: parseInt(form.guest_count, 10) || 1,
        postal_code: form.postal_code.trim(),
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
        website: form.website,
      });
      setSuccess(true);
    } catch (e: any) {
      setSubmitError(e.message ?? "Registration failed. Please try again.");
    }
    setSubmitting(false);
  };

  /* ── Redirect when the event isn't bookable ──────────────────────────
       If the slug doesn't resolve, the load errored, or the event is
       closed/full, send the guest to the marketing site rather than
       showing a dead-end page. Uses location.replace so the bad event
       URL doesn't sit in the back-button history. */
  useEffect(() => {
    if (loading) return;
    if (loadError || !event || !event.is_open) {
      window.location.replace("https://durhamconventioncentre.ca");
    }
  }, [loading, loadError, event]);

  /* ── Loading ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <PageShell>
        <CardSurface>
          <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress sx={{ color: GOLD }} />
            <Typography sx={{ mt: 2.5, color: "rgba(13,27,42,0.55)", letterSpacing: 0.5, fontSize: "0.9rem" }}>
              Loading event details…
            </Typography>
          </Box>
        </CardSurface>
      </PageShell>
    );
  }

  /* ── Load error / 404 — redirect handled by the effect above ─────── */
  if (loadError || !event) {
    return (
      <PageShell>
        <CardSurface>
          <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        </CardSurface>
      </PageShell>
    );
  }

  /* ── Success state ───────────────────────────────────────────────── */
  if (success) {
    return (
      <PageShell>
        <CardSurface>
          {event.banner_image && (
            <Box
              component="img"
              src={event.banner_image}
              alt=""
              // Render at natural aspect ratio (height: auto) instead of
              // a fixed height + objectFit:cover — the marketing banner
              // has titles at the top and footer text at the bottom that
              // a hard-crop would slice off. maxHeight caps absurdly
              // tall uploads while keeping the whole image visible.
              sx={{ width: "100%", height: "auto", maxHeight: { xs: 360, sm: 520 }, objectFit: "contain", display: "block", bgcolor: NAVY }}
            />
          )}
          <Box sx={{ py: { xs: 5, sm: 8 }, px: { xs: 3, sm: 6 }, textAlign: "center" }}>
            <Box
              sx={{
                width: 88, height: 88, borderRadius: "50%",
                bgcolor: "rgba(198,156,106,0.12)",
                border: `1px solid ${HAIRLINE_GOLD}`,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: GOLD }} />
            </Box>
            <Eyebrow>Confirmed</Eyebrow>
            <Typography
              sx={{
                fontFamily: FONT_SERIF, fontWeight: 600, color: NAVY, mt: 2,
                fontSize: { xs: "1.7rem", sm: "2.2rem" }, lineHeight: 1.15,
              }}
            >
              Thank you for registering.
            </Typography>
            <Typography sx={{ mt: 2.5, color: "rgba(13,27,42,0.7)", fontSize: "1.05rem", lineHeight: 1.6, maxWidth: 480, mx: "auto" }}>
              Your RSVP for{" "}
              <Box component="span" sx={{ color: NAVY, fontWeight: 600 }}>{event.title}</Box>
              {" "}has been confirmed.
            </Typography>
            <Typography sx={{ mt: 1.5, color: "rgba(13,27,42,0.55)", fontSize: "0.95rem", maxWidth: 480, mx: "auto" }}>
              A confirmation email is on its way. We look forward to seeing you on{" "}
              {formatEventDate(event.event_date)}.
            </Typography>
            <Ornament sx={{ mt: 5, mb: 4 }} />
            <Typography sx={{ color: "rgba(13,27,42,0.5)", fontSize: "0.78rem", letterSpacing: 1.2, textTransform: "uppercase" }}>
              Durham Convention Centre · 630 Beck Crescent, Ajax, ON
            </Typography>
          </Box>
        </CardSurface>
      </PageShell>
    );
  }

  /* ── Closed / Full — redirect handled by the effect above ────────── */
  if (!event.is_open) {
    return (
      <PageShell>
        <CardSurface>
          <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        </CardSurface>
      </PageShell>
    );
  }

  /* ── Default — registration form ─────────────────────────────────── */
  return (
    <PageShell>
      {/* Branded loading overlay — shown while the registration POST is
          in-flight. Uses the global DCC Loader (spinning ring + logo +
          animated dots) over a tinted ivory backdrop so the form is
          covered until the server response either lands on the success
          state or surfaces an error. */}
      <Backdrop
        open={submitting}
        sx={{
          bgcolor: "rgba(250,248,244,0.94)",
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <Loader height="auto" />
      </Backdrop>

      <EventCard event={event}>
        <Eyebrow>Reserve Your Seat</Eyebrow>
        <Typography
          sx={{
            fontFamily: FONT_SERIF, fontWeight: 600, color: NAVY, mt: 2,
            fontSize: { xs: "1.55rem", sm: "1.85rem" }, lineHeight: 1.2,
          }}
        >
          Register Below
        </Typography>
        <Typography sx={{ color: "rgba(13,27,42,0.6)", mt: 1.5, mb: 4, fontSize: "0.95rem", lineHeight: 1.6 }}>
          Reserve your spot in just a moment. Fields marked with{" "}
          <Box component="span" sx={{ color: GOLD_DEEP, fontWeight: 700 }}>*</Box>{" "}
          are required.
        </Typography>

        {submitError && (
          <Alert
            severity="error"
            sx={{
              mb: 3, borderRadius: 0,
              border: "1px solid rgba(211,47,47,0.25)",
              bgcolor: "rgba(211,47,47,0.05)",
            }}
          >
            {submitError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap={2.5}>
            <TextField
              label="Full Name" value={form.full_name} onChange={handleChange("full_name")}
              required fullWidth sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2.5}>
              <TextField
                label="Email" type="email" value={form.email}
                onChange={handleChange("email")} onBlur={handleBlur("email")}
                error={!!emailError} helperText={emailError ?? undefined}
                required fullWidth sx={fieldSx}
              />
              <TextField
                label="Cell Number" value={form.phone}
                onChange={handleChange("phone")} onBlur={handleBlur("phone")}
                error={!!phoneError} helperText={phoneError ?? undefined}
                required fullWidth sx={fieldSx}
              />
            </Stack>
            <TextField
              label="Organization / Affiliation" value={form.organization} onChange={handleChange("organization")}
              required fullWidth sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2.5}>
              <TextField
                label="Type of Business" value={form.business_type} onChange={handleChange("business_type")}
                required sx={{ ...fieldSx, flex: 2 }}
              />
              <TextField
                label="Number of Guests" value={form.guest_count} onChange={handleChange("guest_count")}
                type="number"
                inputProps={{ min: 1, max: 50, step: 1 }}
                required sx={{ ...fieldSx, flex: 1 }}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2.5}>
              <TextField
                label="Postal Code" value={form.postal_code}
                onChange={handleChange("postal_code")} onBlur={handleBlur("postal_code")}
                error={!!postalError} helperText={postalError ?? undefined}
                required sx={{ ...fieldSx, flex: 1 }}
              />
              <TextField
                label="Address (optional)" value={form.address} onChange={handleChange("address")}
                sx={{ ...fieldSx, flex: 2 }}
              />
            </Stack>
            <TextField
              label="Message / Notes (optional)" value={form.notes} onChange={handleChange("notes")}
              multiline rows={3} fullWidth sx={fieldSx}
            />

            {/* Honeypot — hidden from humans via CSS. Real users never
                see this field; bots that fill every input trip it and
                the server returns a silent fake-success. */}
            <Box sx={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden>
              <input
                type="text" name="website" tabIndex={-1} autoComplete="off"
                value={form.website} onChange={handleChange("website")}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                mt: 2, bgcolor: NAVY, color: "#fff", fontWeight: 700, py: 1.75,
                fontSize: "0.82rem", letterSpacing: 1.8, textTransform: "uppercase",
                borderRadius: 0, boxShadow: "none",
                borderBottom: `3px solid transparent`,
                transition: "all 0.25s ease",
                "&:hover": {
                  bgcolor: NAVY_DEEP, boxShadow: "none",
                  borderBottomColor: GOLD,
                },
                "&.Mui-disabled": { bgcolor: "rgba(13,27,42,0.4)", color: "#fff" },
              }}
            >
              Confirm Registration
            </Button>
            <Typography sx={{ color: "rgba(13,27,42,0.5)", textAlign: "center", fontSize: "0.78rem", lineHeight: 1.55, mt: 1, maxWidth: 460, mx: "auto" }}>
              By registering, you'll receive a confirmation email and event reminders from Durham Convention Centre.
            </Typography>
          </Stack>
        </Box>
      </EventCard>
    </PageShell>
  );
};

/* ──────────────────────────────────────────────────────────────────────
   Layout primitives
   ──────────────────────────────────────────────────────────────────── */

/** Eyebrow — small uppercase gold label flanked by hairlines. Editorial. */
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
    <Box sx={{ width: 22, height: "1px", bgcolor: GOLD }} />
    <Typography sx={{
      color: GOLD_DEEP, fontWeight: 700, letterSpacing: 2.5,
      fontSize: "0.7rem", textTransform: "uppercase", fontFamily: FONT_SANS,
    }}>
      {children}
    </Typography>
    <Box sx={{ width: 22, height: "1px", bgcolor: GOLD }} />
  </Box>
);

/** Diamond ornament — gold rule with three little diamonds in the middle. */
const Ornament: React.FC<{ sx?: any }> = ({ sx }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, ...sx }}>
    <Box sx={{ flex: 1, height: "1px", bgcolor: HAIRLINE, maxWidth: 140 }} />
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
      <Box sx={{ width: 5, height: 5, bgcolor: GOLD, transform: "rotate(45deg)", opacity: 0.55 }} />
      <Box sx={{ width: 7, height: 7, bgcolor: GOLD, transform: "rotate(45deg)" }} />
      <Box sx={{ width: 5, height: 5, bgcolor: GOLD, transform: "rotate(45deg)", opacity: 0.55 }} />
    </Box>
    <Box sx={{ flex: 1, height: "1px", bgcolor: HAIRLINE, maxWidth: 140 }} />
  </Box>
);

/** Plain white surface card with sharp corners and a refined drop-shadow. */
const CardSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      border: `1px solid ${HAIRLINE}`,
      boxShadow: "0 40px 100px -40px rgba(13,27,42,0.28)",
      overflow: "hidden",
    }}
  >
    {children}
  </Box>
);

/** Outer page chrome — ivory bg with a soft warm glow at top, navy header
    with gold accent strip, navy footer with venue details. */
const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: IVORY,
      fontFamily: FONT_SANS,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute", top: 0, left: 0, right: 0, height: 640, zIndex: 0,
        background: `radial-gradient(ellipse 800px 400px at 50% 0%, rgba(198,156,106,0.12), transparent 70%)`,
        pointerEvents: "none",
      },
    }}
  >
    {/* Header — centered logo, scales smaller on mobile so the logo never
        crowds a narrow viewport. */}
    <Box
      component="header"
      sx={{
        position: "relative", zIndex: 1,
        bgcolor: NAVY, color: "#fff",
        py: { xs: 2.5, sm: 3.25 }, px: { xs: 2, sm: 6 },
        display: "flex", alignItems: "center", justifyContent: "center",
        borderBottom: `2px solid ${GOLD}`,
      }}
    >
      <Box
        component="img"
        src="https://durhamconventioncentre.ca/wp-content/uploads/2025/12/Logo-2.webp"
        alt="Durham Convention Centre"
        sx={{
          height: { xs: 56, sm: 76 },
          width: "auto",
          maxWidth: "85%",
          display: "block",
        }}
      />
    </Box>

    {/* Content */}
    <Box sx={{ position: "relative", zIndex: 1, maxWidth: 780, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 4, sm: 7 } }}>
      {children}
    </Box>

    {/* Footer */}
    <Box
      component="footer"
      sx={{
        position: "relative", zIndex: 1,
        bgcolor: NAVY, color: "rgba(255,255,255,0.65)",
        textAlign: "center", py: 4.5, px: 3,
        mt: { xs: 6, sm: 10 },
        borderTop: `2px solid ${GOLD}`,
      }}
    >
      <Typography sx={{
        fontFamily: FONT_SERIF, fontSize: "1.05rem", color: "#fff",
        fontWeight: 600, mb: 1.5, letterSpacing: 0.3,
      }}>
        Durham Convention Centre
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", letterSpacing: 0.4, lineHeight: 1.6 }}>
        630 Beck Crescent, Ajax, ON L1Z 1C9
      </Typography>
      <Box sx={{ width: 32, height: "1px", bgcolor: GOLD, mx: "auto", my: 2.5, opacity: 0.6 }} />
      <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", letterSpacing: 0.8 }}>
        &copy; {new Date().getFullYear()} Durham Convention Centre. All rights reserved.
      </Typography>
    </Box>
  </Box>
);

/** Reusable event card — banner image, eyebrow + title + description,
    cream-tinted facts grid with circular icon badges, gold ornament,
    then body content (form or warning). */
const EventCard: React.FC<{ event: PublicEventPayload; children: React.ReactNode }> = ({ event, children }) => (
  <CardSurface>
    {event.banner_image && (
      <Box
        component="img"
        src={event.banner_image}
        alt=""
        // height: auto + objectFit: contain preserves the banner's
        // natural aspect ratio so titles at the top and DCC footer at
        // the bottom of the artwork aren't sliced off by a hard crop.
        // maxHeight caps tall portrait uploads from dominating the
        // viewport on small screens.
        sx={{ width: "100%", height: "auto", maxHeight: { xs: 400, sm: 560 }, objectFit: "contain", display: "block", bgcolor: NAVY }}
      />
    )}
    <Box sx={{ py: { xs: 4.5, sm: 6 }, px: { xs: 3, sm: 6 } }}>
      <Eyebrow>You&rsquo;re Invited</Eyebrow>
      <Typography
        sx={{
          fontFamily: FONT_SERIF, fontWeight: 600, color: NAVY,
          lineHeight: 1.12, letterSpacing: "-0.5px", mt: 2.5,
          fontSize: { xs: "1.85rem", sm: "2.5rem" },
        }}
      >
        {event.title}
      </Typography>
      {event.description && (
        <Typography sx={{
          mt: 2.5, color: "rgba(13,27,42,0.72)", lineHeight: 1.65,
          fontSize: { xs: "0.98rem", sm: "1.05rem" },
        }}>
          {event.description}
        </Typography>
      )}

      {/* Event facts — cream-tinted band, 2 cols on desktop */}
      <Box
        sx={{
          mt: 4.5,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: { xs: 2.5, sm: 3 },
          py: { xs: 3, sm: 3.5 },
          px: { xs: 3, sm: 4 },
          bgcolor: CREAM,
          border: `1px solid ${HAIRLINE_GOLD}`,
        }}
      >
        <FactCell icon={<EventIcon />}                label="Date"      value={formatEventDate(event.event_date)} />
        {event.event_time && (
          <FactCell icon={<AccessTimeIcon />}         label="Time"      value={event.event_time} />
        )}
        {event.location && (
          <FactCell icon={<LocationOnIcon />}         label="Location"  value={event.location} />
        )}
        {event.admission_text && (
          <FactCell icon={<ConfirmationNumberIcon />} label="Admission" value={event.admission_text} />
        )}
      </Box>

      <Ornament sx={{ my: 5 }} />
      {children}
    </Box>
  </CardSurface>
);

const FactCell: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Stack direction="row" gap={2} alignItems="flex-start">
    <Box
      sx={{
        width: 42, height: 42, borderRadius: "50%",
        bgcolor: "#fff", border: `1px solid ${HAIRLINE_GOLD}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: GOLD, flexShrink: 0,
        "& svg": { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{
        color: GOLD_DEEP, fontWeight: 700, letterSpacing: 1.8,
        textTransform: "uppercase", fontSize: "0.66rem", fontFamily: FONT_SANS,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        color: NAVY, fontSize: { xs: "0.96rem", sm: "1rem" },
        lineHeight: 1.45, mt: 0.5, whiteSpace: "pre-line", fontWeight: 500,
      }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

export default PublicEventRegistration;
